import React, { useRef, useState } from "react";
import type { CreatedTrialInvite } from "../../../services/trialInviteApi";
import { formatTrialDays } from "../../../utils/trialDays";
import { formatDateTime } from "./formatDateTime";

type CopyStatus = "idle" | "copied" | "failed";

interface CreatedInviteNoticeProps {
  created: CreatedTrialInvite;
  /** Called when the link was copied (button or manual Ctrl+C on the field). */
  onCopied: () => void;
}

/**
 * The freshly created link — shown once, never retrievable again. Mount with
 * `key={created.url}` so the copy status resets for every new link.
 */
const CreatedInviteNotice: React.FC<CreatedInviteNoticeProps> = ({
  created,
  onCopied,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [copyStatus, setCopyStatus] = useState<CopyStatus>("idle");

  const markCopied = () => {
    setCopyStatus("copied");
    onCopied();
  };

  const handleCopy = async () => {
    try {
      // navigator.clipboard is undefined outside secure contexts — the
      // resulting TypeError lands in the same catch as a denied permission.
      await navigator.clipboard.writeText(created.url);
      markCopied();
    } catch {
      setCopyStatus("failed");
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  };

  return (
    <div
      className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2"
      role="status"
    >
      <p className="text-sm font-medium text-green-800">
        Link gotowy. Skopiuj go teraz — nie da się go wyświetlić ponownie.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          readOnly
          value={created.url}
          aria-label="Link zaproszenia"
          onFocus={(e) => e.target.select()}
          onCopy={markCopied}
          className="flex-1 min-w-0 text-sm border border-green-300 rounded-md px-3 py-2 bg-white"
        />
        <button
          type="button"
          onClick={handleCopy}
          className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          {copyStatus === "copied" ? "Skopiowano" : "Kopiuj"}
        </button>
      </div>
      {/* Visual feedback is the button label; this announces it. */}
      <p aria-live="polite" className="sr-only">
        {copyStatus === "copied" ? "Skopiowano do schowka." : ""}
      </p>
      {copyStatus === "failed" && (
        <p role="alert" className="text-sm text-red-700">
          Nie udało się skopiować automatycznie. Link jest zaznaczony — skopiuj
          go ręcznie (Ctrl+C / Cmd+C).
        </p>
      )}
      <p className="text-xs text-green-800">
        Okres próbny: {formatTrialDays(created.invite.trialDays)} · link ważny
        do{" "}
        {formatDateTime(created.invite.expiresAt)}
        {created.invite.email ? ` · tylko dla ${created.invite.email}` : ""}
      </p>
    </div>
  );
};

export default CreatedInviteNotice;
